/**
 * 接口文档
 * https://chore-dev.lingvisions.com/api/aiad/doc.html#/default/%E9%94%80%E5%94%AE%E7%AE%A1%E7%90%86%E6%8E%A5%E5%8F%A3/getSalesListUsingGET
 */
import type { VAxios } from '../../define/axios/Axios';

/**
 * 渠道管理接口配置
 */
export const createApi = (defHttp: VAxios) => ({
  // 获取渠道列表
  getSalesChannels: (params: { query?: string }) => {
    return defHttp.get({
      url: `/aiad/pod/sales`,
      params,
    });
  },

  getSalesConvertStatistics: () => {
    return defHttp.get({
      url: `/aiad/pod/sales/convert-stat`,
    });
  },

  getPodSalesOrgsStat: (params: {
    converted?: boolean;
    enable?: boolean;
    endTime?: string;
    orgName?: string;
    page?: number;
    pageSize?: number;
    salesName?: string;
    startTime?: string;
    type?: string;
  }): Promise<{
    convertedCount: number;
    invalidCount: number;
    totalCount: number;
    unconvertedCount: number;
  }> => {
    return defHttp.get({
      url: `/aiad/pod/sales/orgs/stat`,
      params,
    });
  },

  getPodSalesOrgs: (params: {
    converted?: boolean;
    enable?: boolean;
    endTime?: string;
    orgName?: string;
    page?: number;
    pageSize?: number;
    salesName?: string;
    startTime?: string;
    type?: string;
  }): Promise<{
    items: {
      converted: boolean;
      createTime: string;
      enable: boolean;
      id: number;
      lingSalesCode: string;
      orgId: string;
      orgName: string;
      salesChannelCode: string;
      salesCode: string;
      salesName: string;
      type: string;
    }[];
    pages: number;
    total: number;
  }> => {
    return defHttp.get({
      url: `/aiad/pod/sales/orgs`,
      params,
    });
  },

  // 新增渠道人员
  addSalesChannel: (params: { name: string; userId?: string }) => {
    return defHttp.post({
      url: `/aiad/pod/sales`,
      params,
    });
  },

  // 修改渠道人员信息
  updateSalesChannel: (params: {
    name: string;
    salesId: string;
    status?: 'enabled' | 'disabled';
    userId?: string;
  }) => {
    return defHttp.put({
      url: `/aiad/pod/sales`,
      params,
    });
  },

  // 修改对外信息
  updateSalesDisplayName: (params: { displayName: string }) => {
    return defHttp.put({
      url: `/aiad/pod/sales/channels`,
      params,
    });
  },

  // 修改渠道人员状态 (启用/禁用)
  updateSalesStatus: (id: number, status: string) => {
    return defHttp.put({
      url: `/aiad/pod/sales/${id}/status/${status}`,
    });
  },

  postTemplateSharesGroupList(params: {
    endTime?: string;
    groupId?: string;
    name?: string;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
    startTime?: string;
    targetOrgName?: string;
    templateType?: string;
  }): Promise<
    {
      count: number;
      groupId: string;
      groupName: string;
    }[]
  > {
    return defHttp.post({
      url: '/aiad/pod/sales/template-shares/groups/list',
      params,
    });
  },

  postTemplateSharesTemplatesList(params: {
    endTime?: string;
    groupId?: string;
    name?: string;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
    startTime?: string;
    targetOrgName?: string;
    templateType?: string;
  }): Promise<{
    items: {
      groupId: string;
      groupName: string;
      name: string;
      preview: string;
      previews: string[];
      printTagCount: number;
      shareScope: string;
      sharedCustomerCount: number;
      templateCreateTime: string;
      templateId: number;
      templateType: string;
    }[];
    pages: number;
    total: number;
  }> {
    return defHttp.post({
      url: '/aiad/pod/sales/template-shares/templates/list',
      params,
    });
  },

  getPodSalesTemplateShareOrgs(params: { page?: number; pageSize?: number }): Promise<{
    items: {
      converted: boolean;
      createTime: string;
      enable: boolean;
      id: number;
      lingSalesCode: string;
      orgId: string;
      orgName: string;
      salesChannelCode: string;
      salesCode: string;
      salesName: string;
      type: string;
    }[];
    pages: number;
    total: number;
  }> {
    return defHttp.get({
      url: '/aiad/pod/sales/orgs',
      params,
    });
  },

  /**
   * 查询模板分享详情
   * @param templateType
   * @param templateId
   * @param params
   * @returns
   */
  getTemplateSharesTemplateTypeId(param: {
    templateType: string;
    templateId: string;
  }): Promise<void> {
    return defHttp.get({
      url: `/aiad/pod/sales/template-shares/${param.templateType}/${param.templateId}`,
    });
  },

  putTemplateSharesTemplateTypeId(
    templateType: string,
    templateId: string,
    params: {
      shareScope: string;
      targetOrgIds: string[];
    }
  ): Promise<void> {
    return defHttp.put({
      url: `/aiad/pod/sales/template-shares/${templateType}/${templateId}`,
      params,
    });
  },

  deleteTemplateSharesTemplateTypeId(templateType: string, templateId: string): Promise<void> {
    return defHttp.delete({
      url: `/aiad/pod/sales/template-shares/${templateType}/${templateId}`,
    });
  },

  /**
   * 当前客户查询可见供应商列表
   * @param params
   * @returns
   */
  getTemplateSharesSuppliers(params: { templateType?: 'lingtu' | 'psd' | string }): Promise<
    {
      groupCount: string;
      supplierName: string;
      supplierOrgId: string;
      templateCount: string;
    }[]
  > {
    return defHttp.get({
      url: '/aiad/pod/sales/template-shares/shared/suppliers',
      params,
    });
  },

  /**
   * 当前客户查询某供应商的分享模板分组
   * @param supplierOrgId
   * @param params
   * @returns
   */
  getTemplateSharesSuppliersGruopId(
    supplierOrgId: string,
    params: {
      templateType?: string; // 灵图样机(lingtu) 或 PSD 样机(psd)
    }
  ): Promise<
    {
      count: number;
      groupId: string;
      groupName: string;
    }[]
  > {
    return defHttp.get({
      url: `/aiad/pod/sales/template-shares/shared/suppliers/${supplierOrgId}/groups`,
      params,
    });
  },

  /**
   * 当前客户查询某供应商的分享模板列表
   * @param supplierOrgId
   * @param params
   * @returns
   */
  postTemplateSharesSuppliersTempList(
    supplierOrgId: string,
    params: {
      groupId?: string; // 对应 url 的 supplierGroupId 字段
      name?: string;
      page: number;
      pageSize: number;
      sortField?: string;
      sortOrder?: string;
      templateType?: string; // 灵图样机(lingtu) 或 PSD 样机(psd)
      type?: string;
    }
  ): Promise<{
    items: {
      groupId: string;
      groupName: string;
      name: string;
      preview: string;
      previews: string[];
      printTagCount: number;
      shareScope: string;
      sharedCustomerCount: number;
      templateCreateTime: string;
      templateId: string;
      templateType: string;
    }[];
    pages: number;
    total: number;
  }> {
    return defHttp.post({
      url: `/aiad/pod/sales/template-shares/shared/suppliers/${supplierOrgId}/templates/list`,
      params,
    });
  },

  /** 我的热卖品申请列表 */
  getPodHotProductsMy(params?: {
    auditStatus?: 'pending' | 'approved' | 'rejected';
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
  }): Promise<{
    items: {
      auditBy?: string | null;
      auditStatus?: 'pending' | 'approved' | 'rejected';
      auditTime?: string | null;
      categoryName: string;
      cost: number;
      craft: string;
      createTime: string;
      hotTemplateId: number;
      id: number;
      meta: string;
      mockupTemplateId: number;
      mockupType: string;
      preview: string;
      shippingCycle: string;
      shippingPlace: string;
      status?: 'draft' | 'online' | 'offline';
      suggestedPrice: number;
    }[];
    pages: number;
    total: number;
  }> {
    return defHttp.get({
      url: '/aiad/pod/hot-products/my',
      params,
    });
  },

  /** 按套图模版查询是否已申请过热卖 */
  getPodHotProductsByMockupTemplate(mockupTemplateId: string): Promise<{
    auditBy?: string | null;
    auditStatus?: 'pending' | 'approved' | 'rejected';
    auditTime?: string | null;
    categoryName: string;
    cost: number;
    craft: string;
    createTime: string;
    hotTemplateId: number;
    id: number;
    meta: string;
    mockupTemplateId: number;
    mockupType: string;
    preview: string;
    sellingPoint?: string;
    shippingCycle: string;
    shippingPlace: string;
    status?: 'draft' | 'online' | 'offline';
    suggestedPrice: number;
  } | null> {
    return defHttp.get({
      url: `/aiad/pod/hot-products/by-mockup-template/${mockupTemplateId}`,
    });
  },

  /** 申请热卖品 */
  postPodHotProductsApply(params: {
    categoryName: string;
    cost: number;
    craft: string;
    mockupTemplateId: string;
    sellingPoint?: string;
    shippingCycle: string;
    shippingPlace: string;
    suggestedPrice: number;
  }): Promise<{
    auditBy?: string | null;
    auditStatus?: 'pending' | 'approved' | 'rejected';
    auditTime?: string | null;
    categoryName: string;
    cost: number;
    craft: string;
    createTime: string;
    hotTemplateId: number;
    id: number;
    meta: string;
    mockupTemplateId: number;
    mockupType: string;
    preview: string;
    sellingPoint?: string;
    shippingCycle: string;
    shippingPlace: string;
    status?: 'draft' | 'online' | 'offline';
    suggestedPrice: number;
  }> {
    return defHttp.post({
      url: '/aiad/pod/hot-products/apply',
      params,
    });
  },
});
